import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Chip, ChipProps, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";

import { TableCell } from "@shared/components";
import { IntegrationEndpoint } from "@modules/erpIntegrations/types";

const METHOD_COLOR: Record<string, ChipProps["color"]> = {
  GET: "info",
  POST: "success",
  PUT: "warning",
  PATCH: "secondary",
  DELETE: "error",
};

interface EndpointsColumnHandlers {
  onEdit: (endpoint: IntegrationEndpoint) => void;
  onDelete: (endpoint: IntegrationEndpoint) => void;
  onHistory: (endpoint: IntegrationEndpoint) => void;
  onToggleActive: (endpoint: IntegrationEndpoint) => void;
  isMutating: boolean;
}

export const useEndpointsColumns = (
  translate: (key: string) => string,
  handlers: EndpointsColumnHandlers
): GridColDef<IntegrationEndpoint>[] => {
  return React.useMemo<GridColDef<IntegrationEndpoint>[]>(
    () => [
      {
        field: "integration",
        headerName: translate("erp_integrations.endpoints.integration"),
        minWidth: 140,
        flex: 0.6,
        renderCell: ({ row }) => (
          <TableCell.Text value={row.integration_name} textAlign="left" />
        ),
      },
      {
        field: "label",
        headerName: translate("erp_integrations.endpoints.label"),
        minWidth: 160,
        flex: 1,
        renderCell: ({ value }) => <TableCell.Text value={value} textAlign="left" />,
      },
      {
        field: "slug",
        headerName: translate("erp_integrations.endpoints.slug"),
        minWidth: 180,
        flex: 1,
        renderCell: ({ value }) => <TableCell.Text value={value} textAlign="left" />,
      },
      {
        field: "method",
        headerName: translate("erp_integrations.endpoints.method"),
        width: 110,
        renderCell: ({ value }) => (
          <Chip size="small" color={METHOD_COLOR[value] ?? "default"} label={value} />
        ),
      },
      {
        field: "path",
        headerName: translate("erp_integrations.endpoints.path"),
        minWidth: 220,
        flex: 1,
        sortable: false,
        renderCell: ({ value }) => (
          <Tooltip title={value || ""}>
            <Typography variant="body2" noWrap>
              {value}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "active",
        headerName: translate("erp_integrations.endpoints.active"),
        width: 90,
        renderCell: ({ row }) => (
          <Switch
            size="small"
            checked={row.active}
            disabled={handlers.isMutating}
            onClick={(event) => event.stopPropagation()}
            onChange={() => handlers.onToggleActive(row)}
          />
        ),
      },
      {
        field: "created_at",
        headerName: translate("erp_integrations.endpoints.created_at"),
        minWidth: 160,
        renderCell: ({ value }) => <TableCell.Date value={value} />,
      },
      {
        field: "updated_at",
        headerName: translate("erp_integrations.endpoints.updated_at"),
        minWidth: 160,
        renderCell: ({ value }) => <TableCell.Date value={value} />,
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        width: 140,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <>
            <Tooltip title={translate("erp_integrations.actions.view_history")}>
              <IconButton size="small" onClick={() => handlers.onHistory(row)}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={translate("erp_integrations.actions.edit_option")}>
              <IconButton size="small" onClick={() => handlers.onEdit(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={translate("erp_integrations.actions.delete_option")}>
              <IconButton
                size="small"
                color="error"
                disabled={handlers.isMutating}
                onClick={() => handlers.onDelete(row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ],
    [translate, handlers]
  );
};
